import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { PartySelect } from "@/components/catalog/PartySelect";
import { AmountField } from "@/components/catalog/AmountField";
import { CreateSupplierDialog } from "@/components/suppliers/CreateSupplierDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  addableDealPartners,
  allowsAddDealPartner,
  applyPartnerInvestmentAmountEdit,
  applyPartnerInvestmentRatioEdit,
  calculatedInvestmentRatio,
  clampPartnerProfitPercentEdit,
  emptyDealPartnerRow,
  investmentAmountTotal,
  isFixedSingleDealPartner,
  roundProfitPercent,
  showsDealPartners,
  showsPartnerProfitColumns,
  withRecalculatedInvestmentRatios,
  type DealPartnerDraft,
  type ProductDealType,
  type ProductProfitRuleType,
} from "@/lib/catalog/dealPartners";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { listPartyOptions, syncPartiesFromApi, type PartyOption } from "@/lib/parties/partiesStore";
import { cn } from "@/lib/utils";
import { Plus, Trash2, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type Props = {
  dealType?: ProductDealType | null;
  profitRuleType: ProductProfitRuleType | null | undefined;
  partners: DealPartnerDraft[];
  parties: PartyOption[];
  companyParties?: PartyOption[];
  /** Investment cap from Base Purchase Cost (null when base unset). */
  investmentPool?: number | null;
  /**
   * Owner Vehicle + Owner Surplus:
   * Investment defaults to Base Purchase (editable). Partner profit UI hidden.
   */
  ownerSurplusDefaults?: boolean;
  onChange: (partners: DealPartnerDraft[]) => void;
  /** Called after parties list is refreshed (e.g. new supplier created). */
  onPartiesChange?: (parties: PartyOption[]) => void;
};

const headerCellClass =
  "text-[10px] font-medium uppercase tracking-wide text-muted-foreground truncate";

function PercentInput({
  value,
  onChange,
  disabled = false,
  placeholder = "0–100",
  ariaLabel = "Percentage",
  className,
}: {
  value: number | null | undefined;
  onChange: (val: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
}) {
  const [text, setText] = useState<string>(() => (value != null ? String(value) : ""));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setText(value != null ? String(value) : "");
    }
  }, [value]);

  return (
    <div className="relative min-w-0 w-full">
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        className={cn("glass-input h-9 w-full pr-6 text-center tabular-nums font-medium", className)}
        disabled={disabled}
        value={text}
        onBlur={() => {
          if (text.trim() === "") {
            onChange(null);
          } else {
            const n = Number(text);
            if (Number.isFinite(n)) {
              const clamped = Math.max(0, Math.min(100, n));
              setText(String(clamped));
              onChange(clamped);
            } else {
              setText(value != null ? String(value) : "");
            }
          }
          setTimeout(() => {
            if (document.activeElement !== inputRef.current) {
              setText(value != null ? String(value) : "");
            }
          }, 0);
        }}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
          setText(raw);
          if (raw === "") {
            onChange(null);
          } else {
            const n = Number(raw);
            if (Number.isFinite(n)) {
              const clamped = Math.min(100, Math.max(0, n));
              onChange(clamped);
            }
          }
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">
        %
      </span>
    </div>
  );
}

export function DealPartnersSection({
  dealType,
  profitRuleType,
  partners,
  parties,
  companyParties = [],
  investmentPool = null,
  ownerSurplusDefaults = false,
  onChange,
  onPartiesChange,
}: Props) {
  const showProfitCols =
    !ownerSurplusDefaults && showsPartnerProfitColumns(dealType, profitRuleType);
  const rows = addableDealPartners(partners);
  const partnersInvested = investmentAmountTotal(rows);
  const poolCap =
    investmentPool != null && Number.isFinite(investmentPool) ? Number(investmentPool) : null;
  /** Ratio denominator = Base Purchase Cost (not the sum of partner amounts). */
  const ratioDenominator = poolCap != null && poolCap > 0 ? poolCap : partnersInvested;
  const sectionVisible = showsDealPartners(dealType);
  const canAddPartner = allowsAddDealPartner(dealType);
  const fixedSingle = isFixedSingleDealPartner(dealType);
  const isPurchased = dealType === "purchased";
  const canDeletePartners = canAddPartner && rows.length > 1;
  const canCreateSupplier = can(getStoredAuthUser(), "supplier_can_create");
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);

  const defaultCompanyParty = useMemo(() => {
    if (companyParties && companyParties.length > 0) return companyParties[0];
    return (
      parties.find((p) => p.isCompany && (p.type ?? "").toLowerCase() === "both") ||
      parties.find((p) => p.isCompany) ||
      parties[0]
    );
  }, [companyParties, parties]);

  const isPartnership = dealType === "partnership";

  const partnerParties = useMemo(() => {
    return parties.filter((p) => {
      const type = (p.type ?? "").toLowerCase();
      return type === "investor" || type === "all" || type === "both" || Boolean(p.isCompany);
    });
  }, [parties]);

  const selectableParties = useMemo(() => {
    if (isPurchased) {
      if (companyParties && companyParties.length > 0) return companyParties;
      const compList = parties.filter((p) => p.isCompany);
      if (compList.length > 0) return compList;
      return defaultCompanyParty ? [defaultCompanyParty] : [];
    }
    if (isPartnership) {
      if (partnerParties.length > 0) return partnerParties;
    }
    const list = parties.filter((p) => {
      if (p.isCompany) return true;
      const type = (p.type ?? "").toLowerCase();
      return type === "investor" || type === "all" || type === "both" || type === "partner" || type === "supplier";
    });
    if (defaultCompanyParty && !list.some((p) => String(p.id) === String(defaultCompanyParty.id))) {
      list.unshift(defaultCompanyParty);
    }
    return list;
  }, [isPurchased, isPartnership, companyParties, partnerParties, parties, defaultCompanyParty]);

  const gridClass = cn(
    "grid gap-x-2.5 gap-y-1.5 items-center",
    canDeletePartners && showProfitCols
      ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_2.25rem]"
      : canDeletePartners
        ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_2.25rem]"
        : showProfitCols
          ? "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]"
          : "grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]",
  );

  // Always keep at least one partner row when a deal type is selected.
  useEffect(() => {
    if (!sectionVisible) return;
    if (rows.length > 0) return;
    onChange([emptyDealPartnerRow()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionVisible, rows.length, dealType]);

  // Partnership deals: Auto-populate lines for all available partner parties if no assigned partners
  useEffect(() => {
    if (dealType !== "partnership") return;
    if (partnerParties.length === 0) return;

    const hasAssigned = rows.some((r) => r.partyId != null && String(r.partyId).trim() !== "");
    if (hasAssigned) return;

    const equalShare = roundProfitPercent(100 / partnerParties.length);
    const base = poolCap ?? ratioDenominator;
    const shareAmount = base && base > 0 ? Number((base / partnerParties.length).toFixed(2)) : null;

    let allocated = 0;
    const newRows: DealPartnerDraft[] = partnerParties.map((p, idx) => {
      const amt =
        base && base > 0
          ? idx === partnerParties.length - 1
            ? Number((base - allocated).toFixed(2))
            : shareAmount
          : null;
      if (amt != null) allocated += amt;
      return {
        key: `dp-part-${p.id}-${idx}`,
        partyId: String(p.id),
        partyName: p.name,
        investmentAmount: amt,
        investmentRatio: equalShare,
        profitType: "percentage",
        profitValue: equalShare,
        isCompanyOwner: false,
      };
    });

    onChange(newRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealType, partnerParties, poolCap]);

  // Keep investment amounts & ratios synchronized vs Base Purchase Cost / Landed Cost
  useEffect(() => {
    if (!poolCap || poolCap <= 0) return;
    if (rows.length === 0) return;

    const allUnsetOrZero = rows.every(
      (r) => r.investmentAmount == null || r.investmentAmount === 0,
    );
    if (allUnsetOrZero) {
      const equalRatio = roundProfitPercent(100 / rows.length);
      const shareAmount = Number((poolCap / rows.length).toFixed(2));
      let allocated = 0;
      const next = rows.map((r, i) => {
        const amt = i === rows.length - 1 ? Number((poolCap - allocated).toFixed(2)) : shareAmount;
        allocated += amt;
        return {
          ...r,
          investmentAmount: amt,
          investmentRatio: equalRatio,
        };
      });
      onChange(next);
      return;
    }

    const currentTotal = investmentAmountTotal(rows);
    if (Math.abs(currentTotal - poolCap) > 0.01) {
      let allocated = 0;
      const next = rows.map((r, i) => {
        const ratio = r.investmentRatio ?? (100 / rows.length);
        const amt =
          i === rows.length - 1
            ? Number((poolCap - allocated).toFixed(2))
            : Number(((poolCap * ratio) / 100).toFixed(2));
        allocated += amt;
        return {
          ...r,
          investmentAmount: amt,
          investmentRatio: ratio,
        };
      });
      onChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolCap, rows.length]);

  // Purchased deals: Auto-select default company party (where is_company = 1 and type = 'both')
  useEffect(() => {
    if (!isPurchased) return;
    if (rows.length === 0) return;
    if (!defaultCompanyParty) return;

    const targetId = String(defaultCompanyParty.id);
    let changed = false;
    const next = rows.map((row) => {
      if (row.partyId != null && String(row.partyId) === targetId) return row;
      changed = true;
      return { ...row, partyId: targetId, partyName: defaultCompanyParty.name };
    });

    if (changed) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPurchased, defaultCompanyParty, rows.map((r) => String(r.partyId ?? "")).join("|")]);

  // Single partner deals (purchased / owner_vehicle): auto-populate investment amount & ratio if empty
  useEffect(() => {
    if (!fixedSingle) return;
    if (rows.length !== 1) return;
    const row = rows[0];
    if (!row) return;

    const base = poolCap ?? ratioDenominator;
    if (!base || base <= 0) return;

    if (row.investmentAmount == null || row.investmentAmount === 0) {
      const nextAmount = Number(base.toFixed(2));
      const nextRatio = 100;
      onChange([{ ...row, investmentAmount: nextAmount, investmentRatio: nextRatio }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedSingle, poolCap, ratioDenominator, rows.length]);

  // Owner Vehicle / Purchased: never more than one partner row.
  useEffect(() => {
    if (!fixedSingle) return;
    if (rows.length <= 1) return;
    onChange(withRecalculatedInvestmentRatios([rows[0]!], poolCap));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedSingle, rows.length, rows.map((r) => r.key).join("|"), poolCap]);

  // Deal partners always use Percentage profit (Showroom keeps its own type UI).
  useEffect(() => {
    if (ownerSurplusDefaults) return;
    if (!showProfitCols) return;
    let changed = false;
    const next = rows.map((row) => {
      if (row.profitType === "percentage") return row;
      changed = true;
      return { ...row, profitType: "percentage" as const };
    });
    if (changed) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ownerSurplusDefaults,
    showProfitCols,
    rows.length,
    rows.map((r) => `${r.key}:${r.profitType}`).join("|"),
  ]);

  // Keep partyName in sync with party → user name from the parties list (for Paid By labels).
  useEffect(() => {
    if (parties.length === 0) return;
    let changed = false;
    const next = rows.map((row) => {
      if (!row.partyId) return row;
      const party = parties.find((p) => p.id === String(row.partyId));
      const name = party?.name?.trim() || null;
      if (!name || name === (row.partyName ?? null)) return row;
      changed = true;
      return { ...row, partyName: name };
    });
    if (changed) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parties, rows.map((r) => `${r.key}:${r.partyId}`).join("|")]);

  // Keep ratios synchronized vs Base Purchase Cost whenever amounts or base change.
  useEffect(() => {
    if (rows.length === 0) return;
    const next = withRecalculatedInvestmentRatios(rows, poolCap);
    const drifted = next.some((row, i) => {
      const cur = rows[i];
      if (!cur) return true;
      const a = cur.investmentRatio;
      const b = row.investmentRatio;
      if (a == null && b == null) return false;
      if (a == null || b == null) return true;
      return Math.abs(Number(a) - Number(b)) > 0.0005;
    });
    if (drifted) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolCap, rows.map((r) => `${r.key}:${r.investmentAmount ?? ""}`).join("|")]);

  const updateRow = (index: number, patch: Partial<DealPartnerDraft>) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const setInvestmentAmount = (index: number, investmentAmount: number | null) => {
    onChange(applyPartnerInvestmentAmountEdit(rows, index, investmentAmount, poolCap));
  };

  const setInvestmentRatio = (index: number, investmentRatio: number | null) => {
    onChange(applyPartnerInvestmentRatioEdit(rows, index, investmentRatio, poolCap));
  };

  const pendingPartner = pendingRemoveIndex != null ? rows[pendingRemoveIndex] : null;
  const pendingPartyName =
    pendingPartner?.partyId != null
      ? parties.find((p) => p.id === pendingPartner.partyId)?.name
      : null;

  const isOwnerVehicle = dealType === "owner_vehicle";

  return (
    <div className="rounded-xl border border-border/50 bg-card/40 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Users className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              {isOwnerVehicle ? "Seller Information" : "Deal partners"}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {isOwnerVehicle
                ? "Set the seller's details and demand price."
                : fixedSingle
                  ? "Set the partner’s investment share of the purchase."
                  : "Set each partner’s investment share of the purchase."}
            </p>
          </div>
        </div>
        {canAddPartner ? (
          <Button
            type="button"
            variant="glass"
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() =>
              onChange(withRecalculatedInvestmentRatios([...rows, emptyDealPartnerRow()], poolCap))
            }
          >
            <Plus className="h-4 w-4" />
            Add Partner
          </Button>
        ) : null}
      </div>

      {!sectionVisible ? (
        <p className="text-xs text-muted-foreground">Select a deal type to add partners.</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Loading partner row…</p>
      ) : (
        <div className="overflow-x-auto -mx-1 px-1 pb-1">
          <div className={cn(gridClass, "min-w-[36rem]")}>
            <div className={cn(headerCellClass, "flex items-center justify-between w-full")}>
              <span>Party</span>
              {canCreateSupplier && !isPurchased ? (
                <button
                  type="button"
                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-primary hover:bg-primary/10"
                  aria-label="Add supplier"
                  title="Add supplier"
                  onClick={() => setAddSupplierOpen(true)}
                >
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
              ) : null}
            </div>
            <div className={headerCellClass}>Investment</div>
            <div className={headerCellClass}>Ratio %</div>
            {showProfitCols ? <div className={headerCellClass}>Profit %</div> : null}
            {canDeletePartners ? <div className={headerCellClass} aria-hidden /> : null}

            {rows.map((row, index) => {
              const ratio = calculatedInvestmentRatio(row.investmentAmount, ratioDenominator);
              const selectedValue =
                row.partyId != null && String(row.partyId).trim() !== ""
                  ? String(row.partyId)
                  : isPurchased && defaultCompanyParty
                    ? String(defaultCompanyParty.id)
                    : "__none__";

              return (
                <div key={row.key} className="contents">
                  <PartySelect
                    value={selectedValue === "__none__" ? "" : selectedValue}
                    options={selectableParties}
                    disabled={isPurchased}
                    placeholder={
                      isPurchased
                        ? selectableParties.length
                          ? "Select company"
                          : "No company parties"
                        : "Select party"
                    }
                    onChange={(v) => {
                      if (!v) {
                        updateRow(index, { partyId: null, partyName: null });
                        return;
                      }
                      const party = selectableParties.find((p) => String(p.id) === String(v));
                      updateRow(index, {
                        partyId: String(v),
                        partyName: party?.name?.trim() || null,
                      });
                    }}
                  />

                  <AmountField
                    id={`partner-invest-${row.key}`}
                    label="Investment"
                    hideLabel
                    shortLabelInside
                    value={row.investmentAmount}
                    onChange={(investmentAmount) => setInvestmentAmount(index, investmentAmount)}
                    placeholder="0"
                    showShortLabelHint={false}
                    className="min-w-0 w-full"
                    inputClassName="h-9"
                  />

                  <PercentInput
                    value={
                      row.investmentRatio != null
                        ? roundProfitPercent(Number(row.investmentRatio))
                        : ratio != null
                          ? roundProfitPercent(ratio)
                          : null
                    }
                    onChange={(val) => setInvestmentRatio(index, val)}
                    placeholder="0–100"
                    ariaLabel="Investment ratio"
                  />

                  {showProfitCols ? (
                    <PercentInput
                      value={row.profitValue == null ? null : roundProfitPercent(Number(row.profitValue))}
                      disabled={ownerSurplusDefaults}
                      onChange={(val) => {
                        if (val == null) {
                          updateRow(index, { profitType: "percentage", profitValue: null });
                          return;
                        }
                        const clamped = clampPartnerProfitPercentEdit(rows, index, val);
                        updateRow(index, {
                          profitType: "percentage",
                          profitValue: clamped == null ? null : roundProfitPercent(clamped),
                        });
                      }}
                      placeholder="0–100"
                      ariaLabel="Profit percent"
                      className={ownerSurplusDefaults ? "bg-muted/30" : undefined}
                    />
                  ) : null}

                  {canDeletePartners ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 shrink-0 justify-self-end text-destructive hover:bg-destructive/10 hover:text-destructive"
                      aria-label="Remove partner"
                      onClick={() => setPendingRemoveIndex(index)}
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={2.25} />
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ConfirmAlertDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(o) => !o && setPendingRemoveIndex(null)}
        title="Remove this deal partner?"
        description={
          pendingPartyName ? (
            <>
              Remove{" "}
              <strong className="text-foreground font-semibold">{pendingPartyName}</strong> from this
              deal?
            </>
          ) : (
            "Remove this partner from the deal?"
          )
        }
        mode="confirm"
        variant="danger"
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (pendingRemoveIndex !== null && canDeletePartners) {
            const next = rows.filter((_, i) => i !== pendingRemoveIndex);
            onChange(
              withRecalculatedInvestmentRatios(
                next.length ? next : [emptyDealPartnerRow()],
                poolCap,
              ),
            );
          }
          setPendingRemoveIndex(null);
        }}
      />

      <CreateSupplierDialog
        open={addSupplierOpen}
        onOpenChange={setAddSupplierOpen}
        onCreated={async (record) => {
          try {
            const nextParties = await syncPartiesFromApi();
            onPartiesChange?.(nextParties.length ? nextParties : listPartyOptions());

            // Prefer selecting the new party on the first empty partner row.
            const emptyIndex = rows.findIndex((row) => !row.partyId);
            if (emptyIndex >= 0) {
              const next = rows.map((row, i) =>
                i === emptyIndex
                  ? {
                      ...row,
                      partyId: String(record.id),
                      partyName: record.name?.trim() || null,
                    }
                  : row,
              );
              onChange(withRecalculatedInvestmentRatios(next, poolCap));
            }
          } catch (error) {
            console.error("[DealPartners] Failed to refresh parties after supplier create", error);
            toast.error("Supplier saved, but the party list could not be refreshed. Reload the page.");
          }
        }}
      />
    </div>
  );
}
